import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const Logo = ({ reverse = false, className = "" }) => (
  <Link to="/" className={`flex items-center ${className}`} data-testid="spott-logo">
    <img src={reverse ? "/spott-logo-white.png" : "/spott-logo.webp"} alt="Spott Marketing"
      className="h-10 w-auto" />
  </Link>
);

const NAV = [
  { to: "/#servicio-retail-media", label: "Retail Media" },
  { to: "/campanas", label: "Campañas" },
  { to: "/recompensas", label: "Puntos" },
  { to: "/#faq", label: "Preguntas" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-spott-blue border-b border-white/10" data-testid="navbar">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
        <Logo reverse />
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.to}
              className="text-sm font-light text-spott-mid transition-colors hover:text-spott-yellow"
              data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              {(user.role === "admin" || user.role === "agente") && (
                <Link
                  to="/admin"
                  className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-spott-yellow hover:text-spott-yellow"
                  data-testid="nav-admin-link"
                >
                  <Shield size={15} /> Admin
                </Link>
              )}
              <Link
                to="/portal"
                className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-spott-yellow hover:text-spott-yellow"
                data-testid="nav-portal-link"
              >
                <LayoutDashboard size={15} /> Mi portal
              </Link>
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-full p-2 text-spott-mid transition-colors hover:text-spott-yellow"
                data-testid="nav-logout-btn"
                aria-label="Cerrar sesión"
              >
                <LogOut size={17} />
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="text-sm font-medium text-white transition-colors hover:text-spott-yellow"
              data-testid="nav-login-btn"
            >
              Iniciar sesión
            </Link>
          )}
          {pathname !== "/cotizar" && (
            <a
              href="/#propuesta"
              className="rounded-full bg-spott-yellow px-5 py-2.5 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03]"
              data-testid="nav-cotizar-btn"
            >
              Propuesta gratis
            </a>
          )}
        </div>
        <button
          className="text-white lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menú"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-spott-blue px-5 pb-6 lg:hidden" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <a key={n.label} href={n.to} onClick={() => setOpen(false)}
              className="block py-3 text-sm text-spott-mid">{n.label}</a>
          ))}
          {user ? (
            <>
              <Link to="/portal" className="block py-3 text-sm text-white" data-testid="nav-mobile-portal">Mi portal</Link>
              {(user.role === "admin" || user.role === "agente") && (
                <Link to="/admin" className="block py-3 text-sm text-white">Admin</Link>
              )}
              <button onClick={logout} className="block py-3 text-sm text-white">Cerrar sesión</button>
            </>
          ) : (
            <Link to="/login" className="block py-3 text-sm text-white"
              data-testid="nav-mobile-login">Iniciar sesión</Link>
          )}
          <a href="/#propuesta" onClick={() => setOpen(false)}
            className="mt-3 block rounded-full bg-spott-yellow px-5 py-3 text-center text-sm font-bold text-spott-blue"
            data-testid="nav-mobile-cotizar">Propuesta gratis</a>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="relative overflow-hidden bg-spott-blue py-16 text-white grain" data-testid="footer">
    <div className="arc-deco -right-24 -top-24 h-72 w-72" />
    <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-5">
        <Logo reverse />
        <p className="mt-5 max-w-sm text-sm font-light text-spott-mid">
          El sistema operativo del Retail Media en Ecuador. Una sola estrategia, un solo contrato,
          todos los retailers.
        </p>
      </div>
      <div className="lg:col-span-3">
        <h4 className="text-sm font-semibold text-spott-yellow">Plataforma</h4>
        <ul className="mt-4 space-y-2 text-sm font-light text-spott-mid">
          <li><a href="/#propuesta" className="hover:text-white">Propuesta 360 gratis</a></li>
          <li><Link to="/campanas" className="hover:text-white">Reportería de campañas</Link></li>
          <li><Link to="/recompensas" className="hover:text-white">Puntos Spott</Link></li>
          <li><Link to="/portal" className="hover:text-white">Portal del cliente</Link></li>
        </ul>
      </div>
      <div className="lg:col-span-4">
        <h4 className="text-sm font-semibold text-spott-yellow">Contacto</h4>
        <ul className="mt-4 space-y-2 text-sm font-light text-spott-mid">
          <li>comercial@spott.ec</li>
          <li>Quito · Guayaquil · Cuenca, Ecuador</li>
        </ul>
      </div>
    </div>
    <div className="mx-auto mt-12 max-w-7xl border-t border-white/10 px-5 pt-6 text-xs font-light text-spott-mid lg:px-10">
      © {new Date().getFullYear()} Spott. Cotizaciones referenciales en USD, sujetas a disponibilidad de inventario.
    </div>
  </footer>
);
